import { Injectable, inject, signal } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { NavigationApiService } from '../../infrastructure/navigation/navigation-api.service';
import { PermissionRole } from '../../infrastructure/navigation/navigation.model';

/**
 * Permisos de vista previa SOLO en dev (`useMock`): el backend aún no otorga
 * estos permisos de las secciones nuevas, así que se agregan localmente para
 * poder revisarlas. En producción (`useMock:false`) NO se agregan.
 * TODO(backend): quitar cuando el backend otorgue estos permisos.
 */
const DEV_PREVIEW_PERMISSIONS = [
  'gl.geoliftcheck.operator.read',
  'gl.geoliftcare.technician.read',
  'gl.iam.service_tenant.read',
  'gl.iam.service_tenant.create',
  'gl.iam.service_tenant.update',
];

/**
 * Permisos del usuario autenticado. Mantiene un `Set` de `permissionKey` para
 * gatear acciones/botones vía `can('gl.iam.user.create')`.
 */
@Injectable({ providedIn: 'root' })
export class PermissionService {
  private readonly api = inject(NavigationApiService);

  private readonly _keys = signal<ReadonlySet<string>>(new Set());
  private readonly _roles = signal<PermissionRole[]>([]);
  private readonly _loaded = signal(false);

  /** Carga en vuelo compartida, para no pedir permisos más de una vez a la vez. */
  private inFlight$: Observable<boolean> | null = null;

  readonly roles = this._roles.asReadonly();
  /** `true` cuando los permisos ya se intentaron cargar (éxito o error). */
  readonly loaded = this._loaded.asReadonly();

  /**
   * Asegura que los permisos estén cargados y emite cuando lo están.
   * Idempotente: si ya están cargados emite de inmediato; si hay una carga en
   * curso, se comparte. Lo usan el `MainLayout` (al entrar) y el guard de ruta.
   */
  ensureLoaded(): Observable<boolean> {
    if (this._loaded()) return of(true);
    if (!this.inFlight$) {
      this.inFlight$ = this.api.getPermissions().pipe(
        map((p) => ({ keys: [...p.keys], roles: p.roles })),
        // Si falla, resolvemos igual (permisos vacíos) para no colgar el guard;
        // el interceptor ya notificó el error.
        catchError(() => of({ keys: [] as string[], roles: [] as PermissionRole[] })),
        tap(({ keys, roles }) => {
          const set = new Set(keys);
          // Solo en dev: habilita las secciones nuevas aún no otorgadas por el backend.
          if (environment.useMock) DEV_PREVIEW_PERMISSIONS.forEach((k) => set.add(k));
          this._keys.set(set);
          this._roles.set(roles);
          this._loaded.set(true);
          this.inFlight$ = null;
        }),
        map(() => true),
        shareReplay(1),
      );
    }
    return this.inFlight$;
  }

  /** Dispara la carga sin esperar el resultado (uso desde el MainLayout). */
  load(): void {
    this.ensureLoaded().subscribe();
  }

  /** ¿El usuario tiene este permiso? Ej: `can('gl.iam.user.create')`. */
  can(permissionKey: string): boolean {
    return this._keys().has(permissionKey);
  }

  /** ¿Tiene al menos uno de estos permisos? */
  canAny(permissionKeys: string[]): boolean {
    const keys = this._keys();
    return permissionKeys.some((key) => keys.has(key));
  }

  clear(): void {
    this._keys.set(new Set());
    this._roles.set([]);
    this._loaded.set(false);
    this.inFlight$ = null;
  }
}
